import { useCallback, useEffect, useRef, useState } from 'react';
import { coworkService } from '../services/cowork';
import { i18nService } from '../services/i18n';
import type { CoworkMemoryStats, CoworkUserMemoryEntry } from '../types/cowork';

/** 普通事实的查询/写入生命周期；失败保留数据和草稿，身份变更清空并废弃迟到响应。 */
export function useMemoryEntries() {
  const [entries, setEntries] = useState<CoworkUserMemoryEntry[]>([]);
  const [stats, setStats] = useState<CoworkMemoryStats | null>(null);
  const [query, setQuery] = useState(''), [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false), [loading, setLoading] = useState(false), [saving, setSaving] = useState(false);
  const [error, setError] = useState(''), [hasMore, setHasMore] = useState(false);
  const epoch = useRef(0), request = useRef(0), writeLocked = useRef(false);
  const readLocked = useRef(false);
  /** 写入结束后的刷新抢占旧查询；分页不并发，避免重复页及陈旧数据覆盖。 */
  const load = useCallback(async (offset = 0) => {
    if (offset && readLocked.current) return;
    const generation = epoch.current, sequence = ++request.current;
    readLocked.current = true; setLoading(true); setError('');
    try {
      const [rows, totals] = await Promise.all([coworkService.listMemoryEntries({ query: query.trim() || undefined, limit: 100, offset }), coworkService.getMemoryStats()]);
      if (generation !== epoch.current || sequence !== request.current) return;
      setEntries(old => offset ? [...new Map([...old, ...rows].map(row => [row.id, row])).values()] : rows);
      setStats(totals); setHasMore(rows.length === 100);
    } catch (err) {
      if (generation === epoch.current && sequence === request.current) setError(err instanceof Error ? err.message : i18nService.t('memoryFile_unavailable'));
    } finally { if (generation === epoch.current && sequence === request.current) { readLocked.current = false; setLoading(false); } }
  }, [query]);
  useEffect(() => {
    const timer = setTimeout(() => void load(), query ? 300 : 0);
    const clear = () => {
      clearTimeout(timer); epoch.current++; request.current++; writeLocked.current = false; readLocked.current = false;
      setEntries([]); setStats(null); setLoading(false); setSaving(false); setError(''); setDraft(''); setEditingId(null); setEditorOpen(false); setHasMore(false);
    };
    window.addEventListener('auth:identityChanging', clear);
    return () => { clearTimeout(timer); request.current++; window.removeEventListener('auth:identityChanging', clear); };
  }, [load, query]);
  useEffect(() => () => { epoch.current++; }, []);
  /** 只在服务端明确成功后关闭编辑器；同步锁避免同一点击事件重复写入。 */
  const mutate = async (removeId?: string) => {
    if (writeLocked.current || (!removeId && !draft.trim())) return;
    const generation = epoch.current; writeLocked.current = true; setSaving(true); setError('');
    try {
      if (removeId) await coworkService.deleteMemoryEntry({ id: removeId });
      else if (editingId) await coworkService.updateMemoryEntry({ id: editingId, text: draft.trim(), status: 'created', isExplicit: true });
      else await coworkService.createMemoryEntry({ text: draft.trim(), isExplicit: true });
      if (generation !== epoch.current) return;
      if (!removeId || editingId === removeId) { setEditorOpen(false); setDraft(''); setEditingId(null); }
      await load();
    } catch (err) { if (generation === epoch.current) setError(err instanceof Error ? err.message : i18nService.t(removeId ? 'coworkMemoryCrudDeleteFailed' : 'coworkMemoryCrudSaveFailed')); }
    finally { if (generation === epoch.current) { writeLocked.current = false; setSaving(false); } }
  };
  /** 切换编辑对象和关闭时先处理当前未保存内容。 */
  const openEditor = (entry?: CoworkUserMemoryEntry) => {
    if (saving || (editorOpen && draft && !window.confirm(i18nService.t('memoryFile_discardConfirm')))) return;
    setEditingId(entry?.id ?? null); setDraft(entry?.text ?? ''); setEditorOpen(true); setError('');
  };
  const closeEditor = () => {
    if (saving || (draft && !window.confirm(i18nService.t('memoryFile_discardConfirm')))) return;
    setEditorOpen(false); setDraft(''); setEditingId(null);
  };
  return { entries, stats, query, setQuery, draft, setDraft, editingId, editorOpen, loading, saving, error, hasMore,
    reload: () => load(), more: () => load(entries.length), save: () => mutate(), remove: (id: string) => mutate(id), openEditor, closeEditor };
}
